import type { LiveQuote, QuoteConnectionStatus } from './QuoteStore';

export type PriceQuoteReference = Partial<LiveQuote> & {
  price?: number | null;
};

export type PriceFeedStatus = 'LIVE' | 'DELAYED' | 'FROZEN' | 'STALE' | 'MARK';

export type PricePresentation = {
  status: PriceFeedStatus;
  stale: boolean;
  usableForPricing: boolean;
  badgeClassName: string;
  priceClassName: string;
};

export const quoteSubscriptionIdentifier = (
  symbol: string,
  quote?: Pick<PriceQuoteReference, 'sec_type' | 'local_symbol'> | null,
) => String(quote?.sec_type || '').toUpperCase() === 'FUT'
  ? quote?.local_symbol || symbol
  : symbol;

type PricePresentationOptions = {
  quote?: PriceQuoteReference | null;
  connectionStatus: QuoteConnectionStatus;
  hasPrice?: boolean;
  streamed?: boolean;
  now?: number;
  defaultStaleAfterSeconds?: number;
};

export const isQuoteStale = (
  quote?: PriceQuoteReference | null,
  now = Date.now(),
  defaultStaleAfterSeconds = 90,
) => {
  if (!quote) return false;
  if (quote.stale) return true;
  const timestamp = quote.exchange_time || quote.received_at;
  const observedAt = timestamp ? Date.parse(timestamp) : Number.NaN;
  return !Number.isFinite(observedAt)
    || (now - observedAt) / 1000 > (quote.stale_after_seconds ?? defaultStaleAfterSeconds);
};

export const getPricePresentation = ({
  quote,
  connectionStatus,
  hasPrice = quote?.price != null,
  streamed = false,
  now = Date.now(),
  defaultStaleAfterSeconds = 90,
}: PricePresentationOptions): PricePresentation => {
  const disconnected = connectionStatus !== 'CONNECTED';
  const stale = hasPrice && (disconnected || isQuoteStale(quote, now, defaultStaleAfterSeconds));
  const normalizedStatus = String(quote?.data_status || '').toUpperCase();
  const frozen = Boolean(quote?.frozen) || normalizedStatus === 'FROZEN';
  const delayed = Boolean(quote?.delayed) || normalizedStatus === 'DELAYED';

  const status: PriceFeedStatus = stale
    ? 'STALE'
    : frozen
      ? 'FROZEN'
      : delayed
        ? 'DELAYED'
        : streamed
          ? 'LIVE'
          : 'MARK';
  const caution = status === 'STALE' || status === 'DELAYED' || status === 'FROZEN';

  return {
    status,
    stale,
    // Frozen snapshots and stale values are reference-only; never use them to
    // populate an executable price or derive a trailing stop estimate.
    usableForPricing: hasPrice && status !== 'STALE' && status !== 'FROZEN',
    badgeClassName: caution
      ? 'border-amber-400/25 bg-amber-400/10 text-amber-300'
      : 'border-emerald-400/25 bg-emerald-400/10 text-emerald-300',
    priceClassName: caution ? 'text-amber-300' : 'text-emerald-400',
  };
};
